import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '../lib/api';

// LIVE standup surface — the real-time "who's on what right now" board. Backed by
// GET /api/surfaces/team/standup/live (dual-auth, bare snake_case data). DISTINCT from the
// retrospective standup (useTeamStandupSurface / /api/surfaces/team/standup — "yesterday").
//
// The interfaces below are the precise compile-time contract (mirrors the MVP Zod contract
// lib/dashboard/contracts/team-surfaces.ts TeamStandup*). The runtime .parse() is deliberately
// SCOPED (decision D5): it enforces only the never-null identity trio + the members-array shape;
// the mostly-null v2/v3 fields stay loose (.passthrough()) so a legit null can't trip the
// .optional()-vs-.nullish() trap and blank the whole board. The backend GUARANTEES the trio is
// never null (fallback strings), so this throws (→ react-query error → SurfaceError) only on a
// genuine backend break, not on honest v1 nulls.

// Traceability — repo/branch/PR + diffstat behind a touchpoint (v3; honest null in v1).
export interface TeamLiveTrace {
  repo?: string | null;
  branch?: string | null;
  pr?: number | null;
  additions?: number | null;
  deletions?: number | null;
  path?: string | null;
}

export interface TeamLiveItem {
  id: string;
  title: string;
  site?: string | null;
  updated_at?: string | null;
  summary?: string | null;
  trace?: TeamLiveTrace | null;
}

export interface TeamLiveThread {
  name: string;
  status: 'shipped' | 'in_progress' | 'discussed' | 'paused';
  note?: string | null;
  last_touched?: string | null;
  trace?: TeamLiveTrace | null;
}

// A per-member connected-source rollup (24h sources — mirrors the MVP TeamStandupSourceSchema). `site`
// is the shared SITE_LABEL / SourceIcon vocabulary. `workspace: true` marks ONLY the team-level Slack
// entry (it shows on every member's card, labeled workspace-level, never as a personal connection).
// events_24h / last_activity_at count VISIBLE (D1-filtered) rows only — a quiet connected source is
// { connected: true, events_24h: 0, last_activity_at: null }.
export interface TeamLiveSource {
  site: string;
  connected: boolean;
  workspace?: boolean | null;
  events_24h: number;
  last_activity_at?: string | null;
}

export interface TeamLiveMember {
  user_id: string;
  display_name: string;
  display_role: string;
  last_active?: string | null;
  project?: string | null;
  repo?: string | null;
  branch?: string | null;
  state_line?: string | null;
  focus?: { headline: string; detail?: string | null } | null;
  recent?: TeamLiveItem[] | null;
  // recent[] now carries the member's full last-24h feed (not a 3-item slice); recent_truncated is
  // true when it hit the backend's per-member cap, so the card can honestly render "50+ in the last
  // day". sources[] is the per-member connected-source rollup. Both nullish (an older backend omits
  // them → the card falls back to no strip / no "+").
  recent_truncated?: boolean | null;
  sources?: TeamLiveSource[] | null;
  blockers?: string[] | null;
  overlap?: { headline: string; detail?: string | null } | null;
  threads?: TeamLiveThread[] | null;
  // Live "in a meeting" status (backend W3). Present only while the teammate is CURRENTLY in a
  // meeting; `name` is the meeting title ONLY when the viewer was invited (the backend withholds it
  // otherwise — a non-invitee gets `{ name: null }`, i.e. busy-state with no name). Not a feed row:
  // it never goes through recent[]/relativeTime.
  in_meeting?: { name?: string | null } | null;
}

export interface TeamLiveSurfaceData {
  team_name?: string | null;
  generated_at?: string | null;
  members: TeamLiveMember[];
}

// Scoped runtime schema (D5) — trio required + non-null; everything else passes through untouched.
const LiveMemberScoped = z
  .object({
    user_id: z.string(),
    display_name: z.string(),
    display_role: z.string(),
  })
  .passthrough();

const LiveSurfaceScoped = z
  .object({
    members: z.array(LiveMemberScoped).default([]),
  })
  .passthrough();

export function useTeamLiveSurface() {
  const query = useQuery({
    queryKey: ['surfaces', 'team-live'],
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/surfaces/team/standup/live');
      // Extra (mostly-null v2/v3) fields pass through; the cast bridges the loose scoped shape to
      // the precise interface the components consume.
      return LiveSurfaceScoped.parse(raw) as unknown as TeamLiveSurfaceData;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  return { ...query, data: query.data ?? undefined };
}
